import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDownIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface DateTimePickerProps {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  label?: string;
  dateLabel?: string;
  timeLabel?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

export function DateTimePicker({
  value,
  onChange,
  label,
  dateLabel = 'Date',
  timeLabel = 'Time',
  minDate,
  maxDate,
  disabled = false,
  error = false,
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(value);
  const [timeValue, setTimeValue] = React.useState(
    value ? format(value, 'HH:mm:ss') : '00:00:00'
  );

  React.useEffect(() => {
    if (value) {
      setSelectedDate(value);
      setTimeValue(format(value, 'HH:mm:ss'));
    }
  }, [value]);

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      setSelectedDate(undefined);
      onChange(undefined);
      return;
    }

    const [hours, minutes, seconds] = timeValue.split(':').map(Number);
    date.setHours(hours, minutes, seconds);
    setSelectedDate(date);
    onChange(date);
    setOpen(false);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setTimeValue(newTime);

    if (selectedDate) {
      const [hours, minutes, seconds] = newTime.split(':').map(Number);
      const newDate = new Date(selectedDate);
      newDate.setHours(hours, minutes, seconds || 0);
      onChange(newDate);
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label className="text-sm font-medium">{label}</Label>
      )}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex flex-col gap-3 flex-1">
          <Label htmlFor="date-picker" className="px-1 text-xs">
            {dateLabel}
          </Label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                id="date-picker"
                disabled={disabled}
                className={cn(
                  'w-full sm:w-auto justify-between font-normal',
                  !selectedDate && 'text-muted-foreground',
                  error && 'border-destructive',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
                data-testid="date-picker-trigger"
              >
                {selectedDate ? (
                  <>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(selectedDate, 'PPP')}
                  </>
                ) : (
                  <>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    <span>Pick a date</span>
                  </>
                )}
                <ChevronDownIcon className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={(date) =>
                  (minDate && date < minDate) ||
                  (maxDate && date > maxDate) ||
                  false
                }
                initialFocus
                data-testid="date-picker-calendar"
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex flex-col gap-3 shrink-0">
          <Label htmlFor="time-picker" className="px-1 text-xs">
            {timeLabel}
          </Label>
          <Input
            type="time"
            id="time-picker"
            step="1"
            value={timeValue}
            onChange={handleTimeChange}
            disabled={disabled || !selectedDate}
            className={cn(
              'w-full sm:w-32',
              error && 'border-destructive',
              'appearance-none [&::-webkit-calendar-picker-indicator]:hidden'
            )}
            data-testid="time-picker-input"
          />
        </div>
      </div>
    </div>
  );
}